/**
 * Compact emoji + sticker picker (Facebook Messenger style).
 * No external library — uses curated Unicode emoji + jumbo sticker emojis.
 */
import { useEffect, useRef, useState } from "react";
import { Smile, Sticker } from "lucide-react";

const EMOJI_CATEGORIES = {
  "🙂 Caras": ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","😉","😊","😇","🥰","😍","🤩","😘","😗","☺️","😚","😙","🥲","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🤫","🤔","🤐","🤨","😐","😑","😶","😏","😒","🙄","😬","🤥","😌","😔","😪","🤤","😴"],
  "❤️ Amor": ["❤️","🧡","💛","💚","💙","💜","🤎","🖤","🤍","💔","❣️","💕","💞","💓","💗","💖","💘","💝","💟","♥️","💌","💋","💍","💐","🌹"],
  "🙌 Gestos": ["👍","👎","👌","🤌","🤏","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","🖕","👇","👋","🤚","🖐️","✋","🖖","👏","🙌","🤝","🙏","✍️","💅","🤳","💪"],
  "🔥 Cosas": ["🔥","✨","🌟","⭐","💫","💥","💢","💨","💦","💧","🎉","🎊","🎁","🎂","🎈","🪄","🔮","🌙","☀️","⚡","🌈","🌸","🌺","🌻","🌷","🌼","💐"],
  "🐾 Animales": ["🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐨","🐯","🦁","🐮","🐷","🐸","🐵","🙊","🙉","🙈","🐒","🦄","🐝","🦋"],
};

// Sticker set — jumbo emojis with stable IDs (kept on backend as sticker:<id>)
export const STICKER_SET = {
  "love_eyes": "😍",
  "smiling_heart": "🥰",
  "joy": "😂",
  "applause": "👏",
  "fire": "🔥",
  "100": "💯",
  "praying": "🙏",
  "rose": "🌹",
  "kiss": "💋",
  "thumbs_up": "👍",
  "broken_heart": "💔",
  "sparkling_heart": "💖",
  "crystal_ball": "🔮",
  "magic_wand": "🪄",
  "star_struck": "🤩",
  "wink_kiss": "😘",
  "cool": "😎",
  "thinking": "🤔",
  "shocked": "😱",
  "muscle": "💪",
  "moon": "🌙",
  "star": "⭐",
  "rainbow": "🌈",
  "party": "🎉",
};

export const EmojiPicker = ({ onSelect, onClose }) => {
  const [cat, setCat] = useState(Object.keys(EMOJI_CATEGORIES)[0]);
  return (
    <div data-testid="emoji-picker" className="absolute bottom-12 left-0 w-72 bg-[#0f0f0f] border border-[#d4af37]/30 rounded-2xl shadow-2xl z-[120] overflow-hidden">
      <div className="flex border-b border-white/10 overflow-x-auto">
        {Object.keys(EMOJI_CATEGORIES).map((k) => (
          <button
            key={k}
            type="button"
            data-testid={`emoji-cat-${k.split(" ")[0]}`}
            onClick={() => setCat(k)}
            className={`text-xs px-2 py-1.5 whitespace-nowrap ${cat === k ? "bg-[#d4af37]/20 text-[#d4af37]" : "text-neutral-400 hover:text-white"}`}
          >{k.split(" ")[0]}</button>
        ))}
      </div>
      <div className="grid grid-cols-8 gap-0.5 p-2 max-h-52 overflow-y-auto">
        {EMOJI_CATEGORIES[cat].map((e, i) => (
          <button
            key={`${cat}-${i}`}
            type="button"
            data-testid={`emoji-${e}`}
            onMouseDown={(ev) => { ev.preventDefault(); onSelect(e); onClose?.(); }}
            className="text-xl hover:bg-white/10 rounded p-1"
          >{e}</button>
        ))}
      </div>
    </div>
  );
};

export const StickerPicker = ({ onSelect, onClose }) => (
  <div data-testid="sticker-picker" className="absolute bottom-12 left-0 w-72 bg-[#0f0f0f] border border-[#d4af37]/30 rounded-2xl shadow-2xl z-[120] overflow-hidden">
    <div className="px-3 py-1.5 border-b border-white/10 text-xs uppercase tracking-widest text-[#d4af37] flex items-center gap-1">
      <Sticker className="w-3 h-3" /> Stickers
    </div>
    <div className="grid grid-cols-4 gap-1 p-2 max-h-60 overflow-y-auto">
      {Object.entries(STICKER_SET).map(([id, emoji]) => (
        <button
          key={id}
          type="button"
          data-testid={`sticker-${id}`}
          onMouseDown={(ev) => { ev.preventDefault(); onSelect(id, emoji); onClose?.(); }}
          className="text-5xl hover:bg-white/10 rounded-lg p-2 flex items-center justify-center"
          title={id}
        >{emoji}</button>
      ))}
    </div>
  </div>
);

export const EmojiButton = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        data-testid="emoji-button"
        onClick={() => setOpen((o) => !o)}
        className="h-7 w-7 rounded-full bg-white/5 hover:bg-white/15 text-neutral-200 flex items-center justify-center"
        title="Emojis"
      ><Smile className="w-3.5 h-3.5" /></button>
      {open && <EmojiPicker onSelect={onSelect} onClose={() => setOpen(false)} />}
    </div>
  );
};

export const StickerButton = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        data-testid="sticker-button"
        onClick={() => setOpen((o) => !o)}
        className="h-7 w-7 rounded-full bg-white/5 hover:bg-white/15 text-neutral-200 flex items-center justify-center"
        title="Stickers"
      ><Sticker className="w-3.5 h-3.5" /></button>
      {open && <StickerPicker onSelect={onSelect} onClose={() => setOpen(false)} />}
    </div>
  );
};
